import { useEffect } from 'react';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import cx from 'classnames';
import { PoiOption } from 'components/common/types/Planner.types';
import { Transition } from 'consts/transition';
import usePrevious from 'customHooks/usePrevious';
import { DEFAULT_PANEL_LIST_ITEM_SIZE_PX, WITH_RADIUS_INPUT_ITEM_SIZE_PX, CUSTOM_POI_DATA_PROVIDER } from './consts';
import { POIListProps } from './PointsOfInterest.types';
import POIOptionRow from './POIOptionRow';

export const POIList: React.FC<POIListProps> = ({
  currentFocusedItemIndex,
  poiOptions,
  transition,
  isMainLevel,
  navigateForward,
  onTransitionComplete,
  isItemSelected,
  getSelectedPOIIncludeAndRadius,
  getSelectedPOIIncludedExcludedChildrenCounter,
  onChangeRadius,
  onIncludeExcludeChange,
  resetCurrentFocusedItemIndex,
  selectedProvider,
  hideParentIncludeExclude,
  isCustomCategory,
  onDeleteCustomCategory,
  isDeselectAllowed = true,
}) => {
  const prevPoiOptions = usePrevious<PoiOption[]>(poiOptions) ?? [];

  useEffect(() => {
    if (transition !== Transition.NONE) {
      setTimeout(onTransitionComplete, 300);
    }
  }, [transition]);

  const getProviderForOption = (option: PoiOption): string =>
    isCustomCategory?.(option) ? CUSTOM_POI_DATA_PROVIDER : selectedProvider;

  const renderList = (options: PoiOption[], width: number, height: number): React.ReactElement<typeof List> => {
    return (
      <List
        ref={(ref) => ref?.resetAfterIndex(0)}
        height={height}
        width={width}
        itemCount={options.length}
        itemSize={(index: number) =>
          isItemSelected(options[index]) ? WITH_RADIUS_INPUT_ITEM_SIZE_PX : DEFAULT_PANEL_LIST_ITEM_SIZE_PX
        }
        overscanCount={5}
      >
        {(props) => {
          const option = options[props.index];
          const isSelected = isItemSelected(option);
          const { include, radius } = getSelectedPOIIncludeAndRadius(option);
          const { included, excluded } = getSelectedPOIIncludedExcludedChildrenCounter(option);
          const isCustomOption = isMainLevel && isCustomCategory?.(option);

          return (
            <POIOptionRow
              currentFocusedIndex={currentFocusedItemIndex}
              index={props.index}
              style={props.style}
              option={option}
              isMainLevel={isMainLevel}
              navigateForward={navigateForward}
              onChangeRadius={(radiusValue) => onChangeRadius(radiusValue, option, props.index)}
              onIncludeExcludeChange={(item) => onIncludeExcludeChange(item, props.index, getProviderForOption(option))}
              isSelected={isSelected || !!include}
              isIncluded={include}
              radius={radius}
              key={option.name}
              resetCurrentFocusedItemIndex={resetCurrentFocusedItemIndex}
              includedChildrenNumber={included}
              excludedChildrenNumber={excluded}
              hideParentIncludeExclude={hideParentIncludeExclude}
              isCustomPOI={isCustomOption}
              onDeleteCustomCategory={isCustomOption ? () => onDeleteCustomCategory?.(option) : undefined}
              isDeselectAllowed={isDeselectAllowed && !isCustomOption}
            />
          );
        }}
      </List>
    );
  };

  const renderCurrentList = (width: number, height: number): React.ReactElement => {
    return (
      <div
        className={cx('absolute', {
          'translate-x-0': transition === Transition.NONE,
          '-translate-x-full transition-all duration-300 ease-in-out': transition === Transition.FORWARD,
          'translate-x-full transition-all duration-300 ease-in-out': transition === Transition.BACKWARD,
        })}
        data-test-id="current-poi-list"
      >
        {renderList(transition === Transition.NONE ? poiOptions : prevPoiOptions, width, height)}
      </div>
    );
  };

  const renderNextList = (width: number, height: number): React.ReactElement | null => {
    return transition !== Transition.NONE ? (
      <div
        className={cx('absolute', {
          'animate-moveX-to-zero': transition === Transition.FORWARD,
          '-animate-moveX-to-zero': transition === Transition.BACKWARD,
        })}
        data-test-id="next-poi-list"
      >
        {renderList(poiOptions, width, height)}
      </div>
    ) : null;
  };

  return (
    <div className="relative flex flex-1 h-full w-full overflow-hidden">
      <AutoSizer>
        {({ height, width }: Size) => (
          <div data-test-id="poi-list">
            {renderCurrentList(width, height)}
            {renderNextList(width, height)}
          </div>
        )}
      </AutoSizer>
    </div>
  );
};
